import React, {Fragment, useEffect, useState} from 'react';
import {
    Backdrop, Box, Button, Chip,
    CircularProgress, Container, Divider, ExpansionPanel, ExpansionPanelDetails, ExpansionPanelSummary,
    Grid, IconButton, makeStyles, Modal,
    Paper,
    TextField,
    Typography,
} from "@material-ui/core";
import {Alert} from '@material-ui/lab';

import useStyles from '../styles/styles';
import {useMutation, useQuery} from "@apollo/react-hooks";
import {gql} from "apollo-boost";
import {navigate} from '@reach/router';

import dashes from 'add-dashes-to-uuid';
import { revenueFields, adjustmentFields } from "../utils/RevenueMap";
import PanelBox from "../components/TrafficSource/PanelBox";
import useTrafficStyles from "../styles/trafficStyles";
import useFormInputs from "../hooks/useFormInputs";
import _ from "lodash";
import setDate from '../utils/setDate';
import convertToCurrency from '../utils/convertToCurrency';
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import RemoveCircleIcon from "@material-ui/icons/RemoveCircle";
import RenderInput from "../components/TrafficSource/RenderInput";
import theme from "../styles/theme";
import getEnvironment from "../utils/getEnvironment";
import DeleteIcon from "@material-ui/icons/Delete";
import AddBoxIcon from "@material-ui/icons/AddBox";
import CheckIcon from "@material-ui/icons/Check";
import ClearIcon from "@material-ui/icons/Clear";

const useRevenueStyles = makeStyles({
    root: {
        border: '1px solid',

        '& .MuiExpansionPanelSummary-root': {
            padding: theme.spacing(0, 2, 0, 1),
        },
    },
    removeButton: {
        cursor: 'pointer',
        pointerEvents: 'all'
    },
});

const RevenueItem = ({revenueId}) => {
    const env = getEnvironment();
    const classes = useStyles();
    const trafficClasses = useTrafficStyles();
    const scheduleClasses = useRevenueStyles();
    const [expandedPanelsArr, setExpandedPanelsArr] = useState([]);
    const [isError, setIsError] = useState(false);
    const [editView, setEditView] = useState(false);
    const [defaultValues, setDefaultValues] = useState({});
    const [isSaved, setIsSaved] = useState(false);
    const [manualLoad, setManualLoad] = useState(false);
    const id = revenueId.includes('-') ? revenueId : dashes(revenueId);
    const [isModalOpen, setIsModalOpen] = useState('');
    const [adjustmentRows, setAdjustmentRows] = useState([]);

    const onSubmit = () => {
        let error = false;
        setIsError(error);
        let nonRequiredFields = [];
        revenueFields.forEach((section) => {
            section.arr.forEach((item) => {
                if (!item.required) {
                    nonRequiredFields.push(item.key);
                }
            });
        });
        Object.keys(inputs).forEach(key => {
            if (nonRequiredFields.includes(key)) {
                return
            }
            const value = inputs[key]
            if (value === '') {
                console.error(`${key} is a required field.`)
                error = true
            }
        });

        if (!error) {
            openModal('edit');
        } else {
            setIsError(true)
        }
    };
    const {clearAllInputs, inputs, handleSwitchChange, handleFilterChange, handleInputChange, handleSubmit, setInputs} = useFormInputs(onSubmit);

    const openModal = (type) => {
        setIsModalOpen(type);
    };
    const closeModal = () => {
        setIsModalOpen('');
    };

    const {loading, error, data, refetch} = useQuery(gql`
        query RevenuePage($revenueId: UUID!) {
            revenue(id: $revenueId) {
                id
                identityId
                leadDate
                billingDate
                revenueType
                revenueClassification
                trafficSourceId
                affiliateId
                sourceSubCode1
                sourceSubCode2
                sourceSubCode3
                sourceSubCode4
                sourceSubCode5
                billed
                payout
                advCompanyId
                adv1
                adv2
                adv3
                adv4
                adv5
                dateCreated
                dateModified
                loanRequestId
                loanResultId
                campaignDecisionLogId
                formId
                clickBrowserId
                formFieldSubmitBrowserId
                formFieldSubmitFieldId
                formSubmitBrowserId
                formSubmitDataPackageType
                everflowStatus
                everflowStatusExpiration
                everflowAffiliateHash
                everflowOfferHash
                everflowTransactionId
                everflowConversionId
                adjustmentTotalBilled
                adjustmentTotalPayout
                adjustments {
                    id
                    revenueId
                    adjustmentType
                    adjustmentCategory
                    payout
                    billed
                    reason
                }
            }
            allAffiliate {
                nodes {
                    id
                    name
                }
            }
            allTrafficSource {
                nodes {
                    id
                    name
                }
            }
            allCompanies {
                id
                name
            }
            allRevenueClassification: __type(name: "RevenueClassification") {
                enumValues {
                    name
                }
            }
            allRevenueEverflowStatus: __type(name: "RevenueEverflowStatus") {
                enumValues {
                    name
                }
            }
            allAdjustmentCategory: __type(name: "RevenueAdjustmentCategory") {
                enumValues {
                    name
                }
            }
            allAdjustmentType: __type(name: "RevenueAdjustmentType") {
                enumValues {
                    name
                }
            }
            allDataPackageType: __type(name: "DataPackageType") {
                enumValues {
                    name
                }
            }
        }
    `, {
        variables: {
            revenueId: id,
        },
        context: {
            clientName: "kgraphql"
        }
    });

    const [createRevenue, {data: mutationData, loading: mutationLoading}] = useMutation(gql`
        mutation CreateRevenue($input: RevenueMutatorInput!) {
            createRevenue(input: $input) {
                id
                identityId
                leadDate
                billingDate
                revenueType
                revenueClassification
                trafficSourceId
                affiliateId
                sourceSubCode1
                sourceSubCode2
                sourceSubCode3
                sourceSubCode4
                sourceSubCode5
                billed
                payout
                advCompanyId
                adv1
                adv2
                adv3
                adv4
                adv5
                loanRequestId
                loanResultId
                campaignDecisionLogId
                formId
                clickBrowserId
                formFieldSubmitBrowserId
                formFieldSubmitFieldId
                formSubmitBrowserId
                formSubmitDataPackageType
                everflowStatus
                everflowStatusExpiration
                everflowAffiliateHash
                everflowOfferHash
                everflowTransactionId
                everflowConversionId
                adjustments {
                    id
                    revenueId
                    adjustmentType
                    adjustmentCategory
                    payout
                    billed
                    reason
                }
            }
        }
    `, {
        context: {
            clientName: "kgraphql"
        }
    });

    const [deleteRevenue, {loading: deleteRevenueLoading, data: deleteRevenueData}] = useMutation(gql`
        mutation DeleteRevenue(
            $id: UUID!
        ) {
            deleteRevenue(id: $id)
        }
    `, {
        context: {
            clientName: "kgraphql"
        }
    });

    const setData = (objData, objKey) => {
        revenueFields.forEach((section => {
            section.arr.forEach((item => {
                const {key, type, inputType} = item
                if (inputs[item.key] === undefined) {
                    const dataKey = objData[objKey][key];
                    let value = dataKey === '' ? '' : dataKey;
                    if (type === 'switch' && value === "") {
                        value = false;
                    }
                    if (key === 'affiliateName') {
                        const affiliateId = objData[objKey].affiliateId;
                        const affiliateName = objData.allAffiliate.nodes.filter(item => item.id === affiliateId);
                        value = affiliateName.length ? affiliateName[0].name : '';
                    }

                    if (key === 'trafficSourceName') {
                        const trafficSourceId = objData[objKey].trafficSourceId;
                        const trafficSourceName = objData.allTrafficSource.nodes.filter(item => item.id === trafficSourceId);
                        value = trafficSourceName.length ? trafficSourceName[0].name : '';
                    }

                    if (key === 'companyName') {
                        const companyId = objData[objKey].advCompanyId;
                        const companyName = objData.allCompanies.filter(item => item.id === companyId);
                        value = companyName.length ? companyName[0].name : '';
                    }

                    if (_.isObject(value)) {
                        value = value.enum;
                    }

                    if (type === 'date' || type === 'dateTime') {
                        value = setDate(value);
                    }

                    if (inputType && inputType === 'number') {
                        value = convertToCurrency(value);
                    }

                    setInputs(inputs => ({
                        ...inputs,
                        [key]: value
                    }));
                    setDefaultValues(inputs => ({
                        ...inputs,
                        [key]: value
                    }));
                }
            }));
        }));

        const adjustments = data.revenue.adjustments;
        if (adjustments.length) {
            let finalArr = [];
            adjustments.forEach((item) => {
                let obj = {};
                let arr = [];
                adjustmentFields.forEach(row => {
                    obj = {
                        ...row,
                        value: item[row.key]
                    }
                   arr.push(obj);
                });
                arr.push({
                    id: item.id,
                    key: 'id'
                }, {
                    delete: false,
                    key: 'delete'
                });
                finalArr.push(arr);
            });
            setAdjustmentRows(finalArr);
        }
    };

    useEffect(() => {
        if (isSaved) {
            setTimeout(() => setIsSaved(false), 5000);
            setManualLoad(false);
            refetch();

        }
    }, [isSaved]);

    useEffect(() => {
        if (data && Object.keys(data).length && data.revenue) {
            setData(data, 'revenue');
            setIsSaved(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (mutationLoading) {
            closeModal();
        }
    }, [mutationLoading]);

    useEffect(() => {
        if (mutationData && Object.keys(mutationData).length) {
            setEditView(false);
            closeModal();
            setIsSaved(true);
        }
    }, [mutationData])

    useEffect(() => {
        if (deleteRevenueData) {
            setTimeout(() => navigate('/info/revenueitems'), 5000);
        }
    }, [deleteRevenueData])

    const deleteRow = (deleteIndex) => {
        const values = [...adjustmentRows];
        let obj = [...values[deleteIndex]];
        const newSet = obj.map(item => {
            if (item.key === 'delete') {
                item.delete = true;
            }
            return {
                ...item,
            };
        });
        values[deleteIndex] = [...newSet];
        setAdjustmentRows(values);
    };

    const addAdjustmentRow = () => {
        const values = [...adjustmentRows];
        const newSet = adjustmentFields.map(item => {
            return {
                ...item,
                value: ''
            };
        });
        values.push(newSet);
        setAdjustmentRows(values);
    };

    const handleAdjustmentChanges = (idx, rowIdx, newVal) => {
        let values = [...adjustmentRows];
        const obj = [...values[idx]];
        obj[rowIdx] = {
            ...obj[rowIdx],
            value: newVal
        };
        values[idx] = [...obj];
        setAdjustmentRows(values);
    }

    if (loading || mutationLoading || manualLoad || deleteRevenueLoading) {
        return (
            <Backdrop className={classes.backdrop} open={true}>
                <CircularProgress color="inherit"/>
            </Backdrop>
        )
    }

    if (error) {
        return (
            <Paper className={classes.paper}>
                <Alert severity="error">Well, this in embarrassing&hellip; something went wrong.</Alert>
                <p>
                    <a href="/#" onClick={(e) => {
                        e.preventDefault();
                        navigate('/info/revenueitems');
                    }}>Click here to view all revenue items.</a>
                </p>
            </Paper>
        )
    }

    if (!data || !data.revenue) {
        return (
            <>
                <Typography component="h2">
                    Revenue data is invalid
                </Typography>
                <pre>${JSON.stringify(data, null, 2)}</pre>
            </>
        )
    }

    data.revenue = data.revenue || {}

    const RowChanges = ({rowData}) => {
        const {title, arr} = rowData;
        return (
            <Container maxWidth={false} disableGutters className={trafficClasses.rowDiff}>
                {title && (
                    <Box className={trafficClasses.rowDiffSectionHeader}>
                        <Typography variant="body1">
                            <strong>{title}</strong>
                        </Typography>
                    </Box>
                )}

                <Box>
                    {arr.map(item => {
                        const {key, label, type} = item;
                        const newInputKey = inputs[key];
                        const currentInputKey = defaultValues[key];
                        return (
                            <Grid container key={key}
                                  className={`row-grid ${newInputKey !== currentInputKey ? trafficClasses.rowDiffHighlight : ''}`}>
                                {label ? (
                                    <Grid item xs={4}>
                                        <Typography variant="body">
                                            {label}
                                        </Typography>
                                    </Grid>
                                ) : null}
                                <Grid item xs={4}>
                                    {type === 'switch' ? (
                                        <>
                                            {currentInputKey ? (
                                                <CheckIcon style={{color: theme.palette.success.main}}/>
                                            ) : (
                                                <ClearIcon style={{color: theme.palette.error.main}}/>
                                            )}
                                        </>
                                    ) : (
                                        <span>{_.isNil(currentInputKey) || currentInputKey === '' ? 'N/A' : currentInputKey}</span>
                                    )}
                                </Grid>
                                <Grid item xs={4}>
                                    {type === 'switch' ? (
                                        <>
                                            {newInputKey ? (
                                                <CheckIcon style={{color: theme.palette.success.main}}/>
                                            ) : (
                                                <ClearIcon style={{color: theme.palette.error.main}}/>
                                            )}
                                        </>
                                    ) : (
                                        <span>{_.isNil(newInputKey) || newInputKey === '' ? 'N/A' : newInputKey}</span>
                                    )}
                                </Grid>
                            </Grid>
                        );
                    })}
                </Box>
            </Container>
        );
    };

    return (
        <>
            <Typography variant="h2" component="h2" className={classes.monospace}>
               Revenue Item
            </Typography>
            {isSaved ? (
                <Alert severity="success">Updates Saved!</Alert>
            ) : false}
            {isError ? (
                <Alert severity="error">Please add values to the highlighted fields!</Alert>
            ) : false}
            {!isSaved ? (
                <Paper className={classes.paper}>
                    <Container maxWidth={false} className={trafficClasses.root}>
                        <form id="revenue-item-form">
                            <Box className={trafficClasses.headerBox}>
                                <Grid container spacing={3} alignItems="flex-end">
                                    {revenueFields.map((section, idx) => {
                                        const {title, arr} = section;
                                        if (!title) {
                                            return (
                                                <Fragment key={idx.toString()}>
                                                    {arr.map(item => {
                                                        const {key, label} = item;
                                                        return (
                                                            <Grid key={key} item xs={4} className={trafficClasses.header}>
                                                                <Typography variant="body1">
                                                                    <strong>{label}</strong>
                                                                </Typography>
                                                                {!editView ? (
                                                                    <Typography variant="body1">
                                                                        {inputs[key]}
                                                                    </Typography>
                                                                ) : (
                                                                    <RenderInput
                                                                        item={item}
                                                                        value={inputs[key]}
                                                                        data={data}
                                                                        handleFilterChange={handleFilterChange}
                                                                        handleInputChange={handleInputChange}
                                                                        handleSwitchChange={handleSwitchChange}
                                                                        setInputs={setInputs}
                                                                        inputs={inputs}
                                                                    />
                                                                )}
                                                            </Grid>
                                                        );
                                                    })}
                                                </Fragment>
                                            );
                                        }
                                        return null;
                                    })}
                                    {env === 'test' ? (
                                        <Grid item xs={4}
                                              className={`${trafficClasses.header} ${trafficClasses.headerButtons} `}>
                                            {!editView ? (
                                                <>
                                                    <Button variant="contained" color="secondary"
                                                            onClick={() => setEditView(true)}>
                                                        Edit
                                                    </Button>
                                                    <Button variant="outlined" className={classes.deleteButton}
                                                            onClick={() => openModal('delete')} startIcon={<DeleteIcon/>}>
                                                        Delete
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Button variant="contained" color="primary" onClick={handleSubmit}>
                                                        Save Changes
                                                    </Button>
                                                    <Button variant="contained" color="secondary" onClick={() => {
                                                        refetch();
                                                        setEditView(false);
                                                    }}>
                                                        Cancel
                                                    </Button>
                                                </>
                                            )}
                                        </Grid>
                                    ) : null}
                                </Grid>
                            </Box>
                            <Box>
                                <Grid container spacing={3} direction="row" justify="center" alignItems="stretch">
                                    {revenueFields.map((section, idx) => (
                                        section.title &&
                                        <PanelBox
                                            key={`${section.title} -${idx.toString()} `}
                                            panelData={section}
                                            trafficClasses={trafficClasses}
                                            editView={editView}
                                            inputs={inputs}
                                            data={data}
                                            handleFilterChange={handleFilterChange}
                                            handleInputChange={handleInputChange}
                                            handleSwitchChange={handleSwitchChange}
                                            setInputs={setInputs}
                                        />
                                    ))}
                                </Grid>
                            </Box>
                            <Divider style={{ margin: '30px 0' }}/>
                            <Box className={`${classes.filterBox} ${trafficClasses.panelBox}`} style={{marginTop: '10px'}}>
                                <Chip
                                    label={'Adjustments'}
                                    className={classes.filterSectionLabels}
                                />
                                {editView ? (
                                    <Button
                                        style={{ float: 'left' }}
                                        variant="contained"
                                        className={classes.addButton}
                                        onClick={() => addAdjustmentRow()}
                                        size="small"
                                    >
                                        Add Row <AddBoxIcon/>
                                    </Button>
                                ) : null}
                                <Grid container direction="row" alignItems="flex-start" spacing={2}>
                                    <Grid item xs={12} style={{ padding: '10px 0'}}>
                                        {adjustmentRows.map((rows, idx) => {
                                            let isDeleted = rows.filter(obj => obj.delete);
                                            if(isDeleted.length) {
                                                return null;
                                            }
                                            const isExpanded = expandedPanelsArr.length ? expandedPanelsArr.includes(idx) : false;
                                            return (
                                                <ExpansionPanel
                                                    key={idx.toString()}
                                                    id={`adjustments-${idx}`}
                                                    expanded={isExpanded}
                                                    className={scheduleClasses.root}
                                                    onChange={(e, bool) => {
                                                        if (bool) {
                                                            setExpandedPanelsArr(expandedPanelsArr => [...expandedPanelsArr, idx]);
                                                        } else {
                                                            if (expandedPanelsArr.includes(idx)) {
                                                                setExpandedPanelsArr(expandedPanelsArr.filter(panel => panel !== idx));
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <ExpansionPanelSummary
                                                        expandIcon={<ExpandMoreIcon/>}
                                                    >
                                                        <Grid
                                                            container
                                                            direction="row"
                                                            alignItems="center"
                                                            spacing={3}
                                                        >
                                                            <Grid container key={idx.toString()} style={{ margin: '10px 0'}}>
                                                                <Grid item xs={12} md={3}>
                                                                    <Box display="flex" flexDirection="row" alignItems="center">
                                                                        <Box display="flex" flexDirection="column" alignItems="center">
                                                                            {!editView ? (
                                                                                <IconButton
                                                                                    className={scheduleClasses.removeButton}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                    }}>

                                                                                </IconButton>
                                                                            ) : (
                                                                                <IconButton
                                                                                    className={scheduleClasses.removeButton}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        deleteRow(idx);
                                                                                    }}>
                                                                                    <RemoveCircleIcon/>
                                                                                </IconButton>
                                                                            )}
                                                                        </Box>
                                                                        <Box textAlign="left">
                                                                            <Typography variant="body1">
                                                                                <strong>{isExpanded ? 'Close Adjustment' : 'View Adjustment'}</strong>
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </Grid>
                                                                {!isExpanded ? (
                                                                    <Grid item xs={9}>
                                                                        <Grid container>
                                                                            {rows.map((item,rowIdx) => {
                                                                                const {key, label} = item;
                                                                                if (key !== 'id' && key !== 'revenueId') {
                                                                                    return (
                                                                                        <Grid item xs={4} key={rowIdx} style={{ paddingBottom: '10px', textAlign: 'left' }}>
                                                                                            <Typography variant="body1" style={{ paddingBottom: '5px' }}>
                                                                                                <strong>{label}</strong>
                                                                                            </Typography>
                                                                                            <Typography variant="body1">
                                                                                                <span>{_.isNil(item.value) || item.value === '' ? 'N/A' : item.value}</span>
                                                                                            </Typography>
                                                                                        </Grid>
                                                                                    );
                                                                                }
                                                                                return null;
                                                                            })}
                                                                        </Grid>
                                                                    </Grid>
                                                                ) : null}
                                                            </Grid>
                                                        </Grid>
                                                    </ExpansionPanelSummary>
                                                    <ExpansionPanelDetails className={classes.details}>
                                                        <Grid
                                                            container
                                                            direction="row"
                                                            spacing={3}
                                                        >
                                                            {rows.map((item, rowIdx) => {
                                                                const {key, label} = item;
                                                                if (key !== 'id') {
                                                                    return (
                                                                        <Grid item xs={4} key={rowIdx} style={{ paddingBottom: '10px', textAlign: 'left' }}>
                                                                            <Typography variant="body1" style={{ paddingBottom: '5px' }}>
                                                                                <strong>{label}</strong>
                                                                            </Typography>
                                                                            {!editView ? (
                                                                                <Typography variant="body1">
                                                                                    <span>{_.isNil(item.value) || item.value === '' ? 'N/A' : item.value}</span>
                                                                                </Typography>
                                                                            ) : (
                                                                                <RenderInput
                                                                                    item={item}
                                                                                    disabled={item.disabled || false}
                                                                                    value={adjustmentRows[idx][rowIdx].value}
                                                                                    data={data}
                                                                                    handleFilterChange={(newVal) => handleAdjustmentChanges(idx, rowIdx, newVal)}
                                                                                    handleInputChange={(e) => handleAdjustmentChanges(idx, rowIdx, e.target.value)}
                                                                                    handleSwitchChange={handleSwitchChange}
                                                                                    setInputs={setInputs}
                                                                                    inputs={inputs}
                                                                                />
                                                                            )}
                                                                        </Grid>
                                                                    );
                                                                }
                                                                return null;
                                                            })}
                                                        </Grid>
                                                    </ExpansionPanelDetails>
                                                </ExpansionPanel>
                                            );
                                        })}
                                    </Grid>
                                </Grid>
                            </Box>
                        </form>
                    </Container>
                    <Modal
                        open={isModalOpen === 'edit'}
                        onClose={closeModal}
                    >
                        <Container className={`${classes.modal} ${trafficClasses.modal}`} maxWidth={false}>
                            <Typography variant="h5" align="center" gutterBottom>
                                Please confirm your revenue item changes
                            </Typography>
                            <Paper className={classes.paper} style={{marginBottom: 0}}>
                                <Box>
                                    <Grid container className="row-grid">
                                        <Grid item xs={4}/>
                                        <Grid item xs={4}>
                                            <Typography variant="subtitle1" align="center">
                                                CURRENT CHANGES
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="subtitle1" align="center">
                                                NEW CHANGES
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Box>
                                    {revenueFields.map((section, idx) => (
                                        <RowChanges key={section.title} rowData={section}/>
                                    ))}
                                </Box>
                                <Grid item xs className={trafficClasses.headerButtons}>
                                    <Button variant="contained" color="primary" onClick={(e) => {
                                        e.preventDefault();
                                        const affiliateId = data.allAffiliate.nodes.filter(item => item.name === inputs.affiliateName);
                                        const trafficSourceId = data.allTrafficSource.nodes.filter(item => item.name === inputs.trafficSourceName);
                                        const advCompanyId = data.allCompanies.filter(item => item.name === inputs.companyName);

                                        let finalInputs = _.pickBy(inputs, v => v !== '');
                                        let finalAdjustments = []
                                        adjustmentRows.forEach((rows) => {
                                            let obj = {};
                                            rows.forEach((item) => {
                                                if (item.value !== '' && item.key !== 'id') {
                                                    obj = {
                                                        ...obj,
                                                        [item.key]: item.value
                                                    };
                                                } else if (item.key === 'id') {
                                                    obj = {
                                                        ...obj,
                                                        id: item.id,
                                                    };
                                                }

                                                obj = {
                                                    ...obj,
                                                    delete: item.delete || false
                                                };
                                            });
                                            if (Object.keys(obj).length) {
                                                finalAdjustments.push(obj);
                                            }
                                        });
                                        setManualLoad(true);
                                        const mutator = {
                                            ...finalInputs,
                                            id,
                                            leadDate: new Date(finalInputs.leadDate).toISOString(),
                                            billingDate: new Date(finalInputs.billingDate).toISOString(),
                                            revenueClassification: finalInputs.revenueClassification,
                                            trafficSourceId: trafficSourceId.length ? trafficSourceId[0].id : null,
                                            affiliateId: affiliateId.length ? affiliateId[0].id : null,
                                            advCompanyId: advCompanyId.length ? advCompanyId[0].id : null,
                                            billed: parseFloat(finalInputs.billed.substring(1)),
                                            payout: parseFloat(finalInputs.payout.substring(1)),
                                            everflowStatusExpiration: finalInputs.everflowStatusExpiration ? new Date(finalInputs.everflowStatusExpiration).toISOString() : "",
                                            adjustments: finalAdjustments,
                                        };
                                        delete mutator.affiliateName;
                                        delete mutator.adjustmentTotalBilled;
                                        delete mutator.adjustmentTotalPayout;
                                        delete mutator.dateCreated;
                                        delete mutator.dateModified;
                                        delete mutator.trafficSourceName;
                                        delete mutator.companyName;
                                        createRevenue({
                                            variables: {
                                                input: mutator
                                            }
                                        });
                                    }}>
                                        Save
                                    </Button>
                                    <Button variant="outlined" onClick={() => closeModal()}>
                                        Cancel
                                    </Button>
                                </Grid>
                            </Paper>
                        </Container>
                    </Modal>
                    <Modal
                        open={isModalOpen === 'delete'}
                        onClose={closeModal}
                    >
                        <Container className={`${classes.modal}`} maxWidth={false}>
                            <Paper className={classes.paper} style={{marginBottom: 0}}>
                                <Typography variant="h5" align="center" gutterBottom>
                                    Are you sure you want to delete this revenue item?
                                </Typography>
                                <Grid item xs className={trafficClasses.headerButtons}
                                      style={{textAlign: 'center', margin: '1rem 0'}}>
                                    <Button variant="contained" color="primary" onClick={(e) => {
                                        e.preventDefault();
                                        setManualLoad(true);
                                        deleteRevenue({
                                            variables: {
                                                id
                                            }
                                        });
                                    }}>
                                        Confirm
                                    </Button>
                                    <Button variant="outlined" onClick={() => closeModal()}>
                                        Cancel
                                    </Button>
                                </Grid>
                            </Paper>
                        </Container>
                    </Modal>
                </Paper>
            ) : (
                <Paper className={classes.paper}>
                    <Alert severity="warning">Refetching updated data...</Alert>
                    <CircularProgress color="inherit"/>
                </Paper>
            )}
        </>
    );
}

export default RevenueItem
